/**
 * Lexical ↔ AST Bridge
 * Converts between Lexical's data model and our AST representation
 *
 * Handles serialization/deserialization between Lexical editor state and our custom AST format.
 */

import {
  Block,
  ContentAST,
  DividerBlock,
  EncryptedNode,
  HeadingBlock,
  ImageBlock,
  InlineContent,
  LinkNode,
  ListBlock,
  ListItem,
  Mark,
  ParagraphBlock,
  QuoteBlock,
  TextNode,
} from '@shared/ast/astTypes'
import {
  createDivider,
  createEmptyAST,
  createEncryptedNode,
  createHeading,
  createList,
  createListItem,
  createParagraph,
  createQuote,
  createTextNode,
  generateBlockId,
} from '@shared/ast/astUtils'
import type { SerializedEditorState, SerializedElementNode, SerializedLexicalNode, SerializedRootNode } from 'lexical'

import type {
  HeadingLevel,
  SerializedCustomDividerNode,
  SerializedCustomEmbedNode,
  SerializedCustomEncryptedNode,
  SerializedCustomHeadingNode,
  SerializedCustomImageNode,
  SerializedCustomInlineEmbedNode,
  SerializedCustomLinkNode,
  SerializedCustomListItemNode,
  SerializedCustomListNode,
  SerializedCustomParagraphNode,
  SerializedCustomQuoteNode,
} from '../../components/editor/lexical/nodes'
import { logger } from '../../utils/logger'

// Lexical format bitmask constants
const IS_BOLD = 1
const IS_ITALIC = 2
const _IS_STRIKETHROUGH = 4
const IS_UNDERLINE = 8
const _IS_CODE = 16
const _IS_SUBSCRIPT = 32
const _IS_SUPERSCRIPT = 64
const _IS_HIGHLIGHT = 128

// Extended text node with style - we define our own to avoid strict type issues
interface SerializedTextNodeWithStyle {
  type: 'text'
  version: 1
  text: string
  format: number
  mode: 'normal' | 'token' | 'segmented'
  style: string
  detail: number
}

/**
 * Converts AST to Lexical SerializedEditorState
 */
export const astToLexical = (ast: ContentAST): SerializedEditorState => {
  const children = ast.blocks.map((block) => astBlockToLexicalNode(block))

  // Lexical throws on an empty root ("the editor state is empty"), which takes
  // the whole editor down with a blank screen. A file with no blocks is a real
  // possibility (the API creates chapters that way), so give it a line to type on.
  if (children.length === 0) {
    children.push(createLexicalParagraphNode({ type: 'paragraph', id: generateBlockId(), content: [] }))
  }

  const root: SerializedRootNode = {
    type: 'root',
    version: 1,
    children,
    direction: ast.meta.dir === 'rtl' ? 'rtl' : 'ltr',
    format: '',
    indent: 0,
  }

  return {
    root,
  }
}

/**
 * Converts Lexical SerializedEditorState to AST
 */
export const lexicalToAst = (
  state: SerializedEditorState,
  metadata?: {
    lang?: 'he' | 'en' | 'mixed'
    dir?: 'rtl' | 'ltr'
    title?: string
    defaultFontFamily?: string
    defaultFontSize?: string
  },
): ContentAST => {
  const ast = createEmptyAST(metadata?.lang || 'mixed', metadata?.dir || 'ltr')

  if (metadata?.title) {
    ast.meta.title = metadata.title
  }

  // Font defaults are used for comparison but not stored in AST meta
  const defaults = {
    fontFamily: metadata?.defaultFontFamily,
    fontSize: metadata?.defaultFontSize,
  }

  const rootChildren = state.root.children || []
  ast.blocks = rootChildren
    .map((node) => lexicalNodeToAstBlock(node, defaults))
    .filter((block): block is Block => block !== null)

  // Ensure we have at least one block
  if (ast.blocks.length === 0) {
    ast.blocks = [createParagraph()]
  }

  return ast
}

/**
 * Creates a fallback paragraph node when block type is unknown
 */
const createFallbackParagraphNode = (): SerializedCustomParagraphNode => {
  return {
    type: 'custom-paragraph',
    version: 1,
    blockId: generateBlockId(),
    children: [],
    direction: null,
    format: '',
    indent: 0,
    textFormat: 0,
    textStyle: '',
  }
}

/**
 * Converts a single AST block to Lexical node
 */
const astBlockToLexicalNode = (block: Block): SerializedLexicalNode => {
  switch (block.type) {
    case 'heading':
      return createLexicalHeadingNode(block)
    case 'paragraph':
      return createLexicalParagraphNode(block)
    case 'quote':
      return createLexicalQuoteNode(block)
    case 'divider':
      return createLexicalDividerNode(block)
    case 'list':
      return createLexicalListNode(block)
    case 'image':
      return createLexicalImageNode(block)
    case 'embed':
      return {
        type: 'custom-embed',
        version: 1,
        blockId: block.id,
        source: { ...block.source },
      } as SerializedCustomEmbedNode
    default:
      return createFallbackParagraphNode()
  }
}

/**
 * Creates a Lexical paragraph node from AST
 */
const createLexicalParagraphNode = (block: ParagraphBlock): SerializedCustomParagraphNode => {
  return {
    type: 'custom-paragraph',
    version: 1,
    blockId: block.id,
    dir: block.attrs?.dir,
    children: astInlineToLexicalChildren(block.content),
    direction: block.attrs?.dir || null,
    format: block.attrs?.align || '',
    indent: 0,
    textFormat: 0,
    textStyle: '',
  }
}

/**
 * Creates a Lexical heading node from AST
 */
const createLexicalHeadingNode = (block: HeadingBlock): SerializedCustomHeadingNode => {
  return {
    type: 'custom-heading',
    version: 1,
    blockId: block.id,
    level: block.level,
    dir: block.attrs?.dir,
    tag: `h${block.level}` as `h${1 | 2 | 3 | 4 | 5 | 6}`,
    children: astInlineToLexicalChildren(block.content),
    direction: block.attrs?.dir || null,
    format: block.attrs?.align || '',
    indent: 0,
  }
}

/**
 * Creates a Lexical quote node from AST
 */
const createLexicalQuoteNode = (block: QuoteBlock): SerializedCustomQuoteNode => {
  return {
    type: 'custom-quote',
    version: 1,
    blockId: block.id,
    dir: block.attrs?.dir,
    children: astInlineToLexicalChildren(block.content),
    direction: block.attrs?.dir || null,
    format: block.attrs?.align || '',
    indent: 0,
  }
}

/**
 * Creates a Lexical divider node from AST
 */
const createLexicalDividerNode = (block: DividerBlock): SerializedCustomDividerNode => {
  return {
    type: 'custom-divider',
    version: 1,
    blockId: block.id,
    align: block.attrs?.align,
  }
}

/**
 * Creates a Lexical list node from AST
 */
const createLexicalListNode = (block: ListBlock): SerializedCustomListNode => {
  return {
    type: 'custom-list',
    version: 1,
    blockId: block.id,
    ordered: block.ordered,
    dir: block.attrs?.dir,
    listType: block.ordered ? 'number' : 'bullet',
    start: 1,
    tag: block.ordered ? 'ol' : 'ul',
    children: buildLexicalListItems(block.items),
    direction: block.attrs?.dir || null,
    format: '',
    indent: 0,
  }
}

/**
 * Builds Lexical list items from AST items, mirroring Lexical's own nested-list
 * representation: a nested list lives inside an empty "wrapper" list item that
 * follows its parent item (the structure Lexical produces on Tab-indent, and the
 * one buildNestedListItems / isWrapperItem read back).
 *
 * A nested level is bulleted unless the parent item says otherwise
 * (`childrenOrdered`), which is how the markdown renderer reads it too. Note the
 * nested list is its own <ol>/<ul>: flattening nested items into the parent
 * ordered list would number the sub-item and corrupt the outer sequence.
 */
const buildLexicalListItems = (items: ListItem[]): SerializedCustomListItemNode[] => {
  const result: SerializedCustomListItemNode[] = []

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    result.push({
      type: 'custom-listitem',
      version: 1,
      blockId: item.id,
      dir: item.attrs?.dir,
      format: '',
      indent: 0,
      value: i + 1,
      checked: undefined,
      children: astInlineToLexicalChildren(item.content),
      direction: item.attrs?.dir || null,
    })

    if (item.children && item.children.length > 0) {
      const nestedOrdered = item.childrenOrdered === true
      const nestedList: SerializedCustomListNode = {
        type: 'custom-list',
        version: 1,
        blockId: generateBlockId(),
        ordered: nestedOrdered,
        listType: nestedOrdered ? 'number' : 'bullet',
        start: 1,
        tag: nestedOrdered ? 'ol' : 'ul',
        children: buildLexicalListItems(item.children),
        direction: item.attrs?.dir || null,
        format: '',
        indent: 0,
      }
      result.push({
        type: 'custom-listitem',
        version: 1,
        blockId: generateBlockId(),
        format: '',
        indent: 0,
        value: i + 1,
        checked: undefined,
        children: [nestedList],
        direction: null,
      })
    }
  }

  return result
}

/**
 * Creates a Lexical image node from AST
 */
const createLexicalImageNode = (block: ImageBlock): SerializedCustomImageNode => {
  return {
    type: 'custom-image',
    version: 1,
    blockId: block.id,
    url: block.url,
    alt: block.alt,
    width: block.width,
    height: block.height,
    caption: block.caption,
    align: block.attrs?.align,
  }
}

/**
 * Converts AST inline content to Lexical children nodes
 */
const astInlineToLexicalChildren = (content: InlineContent[]): SerializedLexicalNode[] => {
  if (content.length === 0) {
    return [createEmptyLexicalTextNode()]
  }

  const result: SerializedLexicalNode[] = []
  for (const node of content) {
    if (node.type === 'text') {
      // Split \n in text nodes into Lexical TextNode + LineBreakNode sequences.
      // AST stores Shift+Enter line breaks as \n within TextNode.text;
      // Lexical needs separate LineBreakNode elements for them.
      if (node.text.includes('\n')) {
        const parts = node.text.split('\n')
        for (let i = 0; i < parts.length; i++) {
          if (i > 0) {
            result.push({ type: 'linebreak', version: 1 })
          }
          if (parts[i].length > 0) {
            result.push(astTextToLexicalTextNode({ ...node, text: parts[i] }))
          }
        }
      } else {
        result.push(astTextToLexicalTextNode(node))
      }
    } else if (node.type === 'inline-embed') {
      result.push({
        type: 'custom-inline-embed',
        version: 1,
        source: { ...node.source },
      } as SerializedCustomInlineEmbedNode)
    } else if (node.type === 'encrypted') {
      result.push(astEncryptedToLexicalNode(node))
    } else if (node.type === 'link') {
      result.push(astLinkToLexicalNode(node))
    } else {
      result.push(createEmptyLexicalTextNode())
    }
  }
  return result.length > 0 ? result : [createEmptyLexicalTextNode()]
}

/**
 * Creates an empty Lexical text node
 */
const createEmptyLexicalTextNode = (): SerializedTextNodeWithStyle => {
  return {
    type: 'text',
    version: 1,
    text: '',
    format: 0,
    mode: 'normal',
    style: '',
    detail: 0,
  }
}

/**
 * Converts AST text node to Lexical text node
 */
const astTextToLexicalTextNode = (node: TextNode): SerializedTextNodeWithStyle => {
  const { format, style } = astMarksToLexicalFormat(node.marks)

  return {
    type: 'text',
    version: 1,
    text: node.text,
    format,
    mode: 'normal',
    style,
    detail: 0,
  }
}

/**
 * Converts AST encrypted node to Lexical node
 */
const astEncryptedToLexicalNode = (node: EncryptedNode): SerializedCustomEncryptedNode => {
  // Combine all text content from the node
  const encryptedText = node.content
    .filter((n): n is TextNode => n.type === 'text')
    .map((n) => n.text)
    .join('')

  return {
    type: 'custom-encrypted',
    version: 1,
    encryptionId: node.id,
    encryptedText,
    algo: node.algo,
    ...(node.character ? { character: node.character } : {}),
    ...(node.authorHeld ? { authorHeld: true as const } : {}),
  }
}

/**
 * Converts AST link node to Lexical node
 */
const astLinkToLexicalNode = (node: LinkNode): SerializedCustomLinkNode => {
  return {
    type: 'custom-link',
    version: 1,
    url: node.url,
    target: '_blank',
    rel: 'noopener noreferrer',
    children: node.content.map((textNode) => astTextToLexicalTextNode(textNode)),
    direction: null,
    format: '',
    indent: 0,
  }
}

/**
 * Converts AST marks to Lexical format bitmask and style string.
 * Default font/size are NOT injected — CSS inheritance from the editor container handles defaults.
 * Only explicit marks (font, fontSize, color) that differ from the default produce inline styles.
 */
const astMarksToLexicalFormat = (marks?: Mark[]): { format: number; style: string } => {
  let format = 0
  const styles: string[] = []

  if (!marks || marks.length === 0) {
    return { format, style: '' }
  }

  for (const mark of marks) {
    if (typeof mark === 'string') {
      switch (mark) {
        case 'bold':
          format |= IS_BOLD
          break
        case 'italic':
          format |= IS_ITALIC
          break
        case 'underline':
          format |= IS_UNDERLINE
          break
      }
    } else if (mark.type === 'font') {
      styles.push(`font-family: ${mark.family}`)
    } else if (mark.type === 'fontSize') {
      styles.push(`font-size: ${mark.value}pt`)
    } else if (mark.type === 'color') {
      styles.push(`color: ${mark.value}`)
    }
  }

  return { format, style: styles.join('; ') }
}

/**
 * Converts Lexical node to AST block
 */
const lexicalNodeToAstBlock = (
  node: SerializedLexicalNode,
  defaults?: { fontFamily?: string; fontSize?: string },
): Block | null => {
  const nodeType = node.type

  switch (nodeType) {
    case 'custom-paragraph':
      return lexicalParagraphToAstBlock(node as SerializedCustomParagraphNode, defaults)
    case 'custom-heading':
      return lexicalHeadingToAstBlock(node as SerializedCustomHeadingNode, defaults)
    case 'custom-quote':
      return lexicalQuoteToAstBlock(node as SerializedCustomQuoteNode, defaults)
    case 'custom-divider':
      return lexicalDividerToAstBlock(node as SerializedCustomDividerNode)
    case 'custom-list':
      return lexicalListToAstBlock(node as SerializedCustomListNode, defaults)
    case 'custom-image':
      return lexicalImageToAstBlock(node as SerializedCustomImageNode)
    case 'custom-embed': {
      const embed = node as SerializedCustomEmbedNode
      return { type: 'embed', id: embed.blockId || generateBlockId(), source: { ...embed.source } }
    }
    case 'custom-section-divider':
      return null
    // Handle standard Lexical node types (fallback)
    case 'paragraph':
      return lexicalStandardParagraphToAstBlock(node as SerializedElementNode, defaults)
    case 'heading':
      return lexicalStandardHeadingToAstBlock(node as any, defaults)
    case 'quote':
      return lexicalStandardQuoteToAstBlock(node as SerializedElementNode, defaults)
    case 'list':
      return lexicalStandardListToAstBlock(node as any, defaults)

    default:
      // Unknown node type - skip
      logger.warn(`Unknown Lexical node type: ${nodeType}`)
      return null
  }
}

/**
 * Converts Lexical custom paragraph to AST
 */
const lexicalParagraphToAstBlock = (
  node: SerializedCustomParagraphNode,
  defaults?: { fontFamily?: string; fontSize?: string },
): ParagraphBlock => {
  const paragraph = createParagraph()
  paragraph.id = node.blockId || generateBlockId()
  paragraph.content = lexicalChildrenToAstInline(node.children || [], defaults)

  if (node.dir || node.format) {
    paragraph.attrs = {}
    if (node.dir) {
      paragraph.attrs.dir = node.dir
    }
    if (node.format && typeof node.format === 'string' && ['left', 'right', 'center'].includes(node.format)) {
      paragraph.attrs.align = node.format as 'left' | 'right' | 'center'
    }
  }

  return paragraph
}

/**
 * Converts Lexical custom heading to AST
 */
const lexicalHeadingToAstBlock = (
  node: SerializedCustomHeadingNode,
  defaults?: { fontFamily?: string; fontSize?: string },
): HeadingBlock => {
  const heading = createHeading(node.level || 1)
  heading.id = node.blockId || generateBlockId()
  heading.content = lexicalChildrenToAstInline(node.children || [], defaults)

  if (node.dir || node.format) {
    heading.attrs = {}
    if (node.dir) {
      heading.attrs.dir = node.dir
    }
    if (node.format && typeof node.format === 'string' && ['left', 'right', 'center'].includes(node.format)) {
      heading.attrs.align = node.format as 'left' | 'right' | 'center'
    }
  }

  return heading
}

/**
 * Converts Lexical custom quote to AST
 */
const lexicalQuoteToAstBlock = (
  node: SerializedCustomQuoteNode,
  defaults?: { fontFamily?: string; fontSize?: string },
): QuoteBlock => {
  const quote = createQuote()
  quote.id = node.blockId || generateBlockId()
  quote.content = lexicalChildrenToAstInline(node.children || [], defaults)

  if (node.dir || node.format) {
    quote.attrs = {}
    if (node.dir) {
      quote.attrs.dir = node.dir
    }
    if (node.format && typeof node.format === 'string' && ['left', 'right', 'center'].includes(node.format)) {
      quote.attrs.align = node.format as 'left' | 'right' | 'center'
    }
  }

  return quote
}

/**
 * Converts Lexical custom divider to AST
 */
const lexicalDividerToAstBlock = (node: SerializedCustomDividerNode): DividerBlock => {
  const divider = createDivider()
  divider.id = node.blockId || generateBlockId()

  if (node.align) {
    divider.attrs = { align: node.align }
  }

  return divider
}

/**
 * Converts Lexical custom list to AST
 * Handles Lexical's indent-based nesting (ListPlugin uses indent property, not nested lists)
 */
const lexicalListToAstBlock = (
  node: SerializedCustomListNode,
  defaults?: { fontFamily?: string; fontSize?: string },
): ListBlock => {
  const list = createList(node.ordered)
  list.id = node.blockId || generateBlockId()

  // Filter to get only list items
  const flatItems = (node.children || []).filter(
    (child): child is SerializedCustomListItemNode => child.type === 'custom-listitem' || child.type === 'listitem',
  )

  // Convert flat items with indent property to nested structure
  list.items = buildNestedListItems(flatItems, node.ordered, defaults)

  if (node.dir) {
    list.attrs = { dir: node.dir }
  }

  // Ensure we have at least one item
  if (list.items.length === 0) {
    list.items = [createListItem()]
  }

  return list
}

/**
 * Helper to check if a Lexical listitem is a "wrapper" item
 * Wrapper items are empty (no text content) but contain a nested list
 * They're created by Lexical when Tab indentation is used
 */
const isWrapperItem = (node: SerializedCustomListItemNode): boolean => {
  const children = node.children || []
  const hasNestedList = children.some((c) => c.type === 'custom-list' || c.type === 'list')
  const hasTextContent = children.some(
    (c) => c.type === 'text' || (c.type !== 'custom-list' && c.type !== 'list' && (c as any).children?.length > 0),
  )
  return hasNestedList && !hasTextContent
}

/**
 * Helper to extract nested list from a wrapper item
 */
const getNestedListFromWrapper = (node: SerializedCustomListItemNode): SerializedCustomListNode | null => {
  for (const child of node.children || []) {
    if (child.type === 'custom-list' || child.type === 'list') {
      return child as SerializedCustomListNode
    }
  }
  return null
}

/**
 * Builds nested AST list items from Lexical's structure
 * Handles both:
 * 1. Flat indent-based structure (indent property)
 * 2. Nested list structure (wrapper items containing nested lists - created by Tab)
 */
const buildNestedListItems = (
  flatItems: SerializedCustomListItemNode[],
  ordered: boolean,
  defaults?: { fontFamily?: string; fontSize?: string },
): ListItem[] => {
  const result: ListItem[] = []

  for (let i = 0; i < flatItems.length; i++) {
    const node = flatItems[i]
    const indent = (node as any).indent || 0

    // Check if this is a wrapper item (created by Tab indentation)
    // Wrapper items should NOT become separate AST items - their children go to previous sibling
    if (isWrapperItem(node) && result.length > 0) {
      const nestedList = getNestedListFromWrapper(node)
      if (nestedList) {
        const previousItem = result[result.length - 1]
        const nestedItems = (nestedList.children || []).filter(
          (child): child is SerializedCustomListItemNode =>
            child.type === 'custom-listitem' || child.type === 'listitem',
        )
        // The nested list carries its own type — a numbered list can hold a
        // bulleted sub-list and vice versa, so read it rather than inheriting.
        const nestedOrdered = nestedList.listType === 'number' || nestedList.ordered === true
        // Recursively process nested items and attach as children of previous sibling
        const nestedAstItems = buildNestedListItems(nestedItems, nestedOrdered, defaults)
        previousItem.children = previousItem.children ? [...previousItem.children, ...nestedAstItems] : nestedAstItems
        if (nestedOrdered) {
          previousItem.childrenOrdered = true
        }
      }
      continue // Don't add wrapper as separate item
    }

    if (indent === 0) {
      // Top-level item - convert and add to result
      const item = lexicalListItemToAstItem(node, ordered, defaults)

      // Collect all following items with indent > 0 as children
      const childItems: SerializedCustomListItemNode[] = []
      let j = i + 1
      while (j < flatItems.length && ((flatItems[j] as any).indent || 0) > 0) {
        childItems.push(flatItems[j])
        j++
      }

      if (childItems.length > 0) {
        // Recursively build nested children (adjusting indent levels).
        // Indent-based nesting keeps everything in one Lexical list node, so the
        // children share the parent list's type.
        const adjustedChildren = childItems.map((child) => ({
          ...child,
          indent: ((child as any).indent || 1) - 1,
        })) as SerializedCustomListItemNode[]
        item.children = item.children
          ? [...item.children, ...buildNestedListItems(adjustedChildren, ordered, defaults)]
          : buildNestedListItems(adjustedChildren, ordered, defaults)
        if (ordered) {
          item.childrenOrdered = true
        }
      }

      result.push(item)
      i = j - 1 // Skip the children we just processed
    }
  }

  return result
}

/**
 * Converts Lexical list item to AST list item (without handling nesting - that's done by buildNestedListItems)
 */
const lexicalListItemToAstItem = (
  node: SerializedCustomListItemNode,
  ordered: boolean,
  defaults?: { fontFamily?: string; fontSize?: string },
): ListItem => {
  const item = createListItem()
  item.id = node.blockId || generateBlockId()

  // Separate inline content from nested lists (for backwards compatibility with nested list structures)
  const inlineChildren: SerializedLexicalNode[] = []
  let nestedList: SerializedCustomListNode | null = null

  for (const child of node.children || []) {
    if (child.type === 'custom-list' || child.type === 'list') {
      nestedList = child as SerializedCustomListNode
    } else {
      inlineChildren.push(child)
    }
  }

  item.content = lexicalChildrenToAstInline(inlineChildren, defaults)

  // Handle nested list structures (created by Tab indentation)
  // Items in nested lists are already semantically nested, so convert them directly
  if (nestedList) {
    const nestedItems = (nestedList.children || []).filter(
      (child): child is SerializedCustomListItemNode => child.type === 'custom-listitem' || child.type === 'listitem',
    )
    // Directly convert each nested item (they're already at the correct nesting level)
    item.children = nestedItems.map((nestedItem) => lexicalListItemToAstItem(nestedItem, ordered, defaults))
  }

  if (node.dir) {
    item.attrs = { dir: node.dir }
  }

  return item
}

/**
 * Converts Lexical custom image to AST
 */
const lexicalImageToAstBlock = (node: SerializedCustomImageNode): ImageBlock => {
  return {
    type: 'image',
    id: node.blockId || generateBlockId(),
    url: node.url,
    alt: node.alt,
    width: node.width,
    height: node.height,
    caption: node.caption,
    attrs: node.align ? { align: node.align } : undefined,
  }
}

/**
 * Converts Lexical children to AST inline content
 */
const lexicalChildrenToAstInline = (
  children: SerializedLexicalNode[],
  defaults?: { fontFamily?: string; fontSize?: string },
): InlineContent[] => {
  const content: InlineContent[] = []

  for (const child of children) {
    if (child.type === 'text') {
      const textNode = lexicalTextToAstTextNode(child as SerializedTextNodeWithStyle, defaults)
      if (textNode) {
        content.push(textNode)
      }
    } else if (child.type === 'custom-inline-embed') {
      content.push({ type: 'inline-embed', source: { ...(child as SerializedCustomInlineEmbedNode).source } })
    } else if (child.type === 'custom-encrypted') {
      content.push(lexicalEncryptedToAstNode(child as SerializedCustomEncryptedNode, defaults))
    } else if (child.type === 'custom-link' || child.type === 'link') {
      content.push(lexicalLinkToAstNode(child as SerializedCustomLinkNode, defaults))
    } else if (child.type === 'linebreak') {
      // Convert linebreaks to newline in text
      content.push(createTextNode('\n'))
    }
  }

  // Ensure we have at least an empty text node
  if (content.length === 0) {
    content.push(createTextNode(''))
  }

  // Filter out empty text nodes unless they're the only node
  return content.filter((node, index, arr) => {
    if (node.type !== 'text') {
      return true
    }
    if (arr.length === 1) {
      return true
    }
    return node.text.length > 0
  })
}

/**
 * Converts Lexical text node to AST text node
 */
const lexicalTextToAstTextNode = (
  node: SerializedTextNodeWithStyle,
  defaults?: { fontFamily?: string; fontSize?: string },
): TextNode | null => {
  const marks = lexicalFormatToAstMarks(node.format, node.style, defaults)
  return createTextNode(node.text, marks.length > 0 ? marks : undefined)
}

/**
 * Converts Lexical encrypted node to AST
 */
const lexicalEncryptedToAstNode = (
  node: SerializedCustomEncryptedNode,
  _defaults?: { fontFamily?: string; fontSize?: string },
): EncryptedNode => {
  // The encrypted node now stores text directly as encryptedText property
  const textNode = createTextNode(node.encryptedText || '')
  return createEncryptedNode([textNode], node.encryptionId, {
    character: node.character,
    ...(node.authorHeld ? { authorHeld: true as const } : {}),
  })
}

/**
 * Converts Lexical link node to AST
 */
const lexicalLinkToAstNode = (
  node: SerializedCustomLinkNode,
  defaults?: { fontFamily?: string; fontSize?: string },
): LinkNode => {
  const textNodes = (node.children || [])
    .filter((child): child is SerializedTextNodeWithStyle => child.type === 'text')
    .map((child) => lexicalTextToAstTextNode(child, defaults))
    .filter((textNode): textNode is TextNode => textNode !== null)

  return {
    type: 'link',
    url: node.url,
    content: textNodes.length > 0 ? textNodes : [createTextNode('')],
  }
}

/**
 * Converts Lexical format bitmask and style to AST marks
 */
const lexicalFormatToAstMarks = (
  format: number,
  style?: string,
  defaults?: { fontFamily?: string; fontSize?: string },
): Mark[] => {
  const marks: Mark[] = []

  // Convert format bitmask to marks
  // Note: strikethrough and code are not supported in AST Mark type
  if (format & IS_BOLD) {
    marks.push('bold')
  }
  if (format & IS_ITALIC) {
    marks.push('italic')
  }
  if (format & IS_UNDERLINE) {
    marks.push('underline')
  }

  // Parse style string for font and color information
  if (style) {
    const styleMap = parseStyleString(style)

    // Only add font mark if different from default
    const fontFamily = styleMap['font-family']
    if (fontFamily && fontFamily !== defaults?.fontFamily) {
      // Map font family to allowed values
      const normalizedFont = normalizeFontFamily(fontFamily)
      if (normalizedFont) {
        marks.push({ type: 'font', family: normalizedFont })
      }
    }

    // Only add fontSize mark if different from default
    const fontSize = styleMap['font-size']
    if (fontSize) {
      const sizeValue = fontSize.replace('pt', '').trim()
      if (sizeValue !== defaults?.fontSize) {
        marks.push({ type: 'fontSize', value: sizeValue })
      }
    }

    // Add color mark
    const color = styleMap['color']
    if (color) {
      marks.push({ type: 'color', value: color })
    }
  }

  return marks
}

/**
 * Parses a CSS style string into a map
 */
const parseStyleString = (style: string): Record<string, string> => {
  const styleMap: Record<string, string> = {}
  const parts = style.split(';')

  for (const part of parts) {
    const colonIndex = part.indexOf(':')
    if (colonIndex > 0) {
      const key = part.substring(0, colonIndex).trim().toLowerCase()
      const value = part.substring(colonIndex + 1).trim()
      styleMap[key] = value
    }
  }

  return styleMap
}

/**
 * Normalizes font family to allowed values
 */
const normalizeFontFamily = (fontFamily: string): 'Arial' | 'Times' | 'Helvetica' | 'Georgia' | 'Verdana' | null => {
  const normalized = fontFamily.toLowerCase().replace(/['"]/g, '')

  if (normalized.includes('arial')) {
    return 'Arial'
  }
  if (normalized.includes('times')) {
    return 'Times'
  }
  if (normalized.includes('helvetica')) {
    return 'Helvetica'
  }
  if (normalized.includes('georgia')) {
    return 'Georgia'
  }
  if (normalized.includes('verdana')) {
    return 'Verdana'
  }

  return null
}

// Standard Lexical fallback converters (for non-custom nodes)

/**
 * Converts standard Lexical paragraph to AST
 */
const lexicalStandardParagraphToAstBlock = (
  node: SerializedElementNode,
  defaults?: { fontFamily?: string; fontSize?: string },
): ParagraphBlock => {
  const paragraph = createParagraph()
  paragraph.content = lexicalChildrenToAstInline(node.children || [], defaults)
  return paragraph
}

/**
 * Converts standard Lexical heading to AST
 */
const lexicalStandardHeadingToAstBlock = (
  node: any,
  defaults?: { fontFamily?: string; fontSize?: string },
): HeadingBlock => {
  const tag = node.tag || 'h1'
  const level = parseInt(tag.replace('h', ''), 10) as HeadingLevel
  const heading = createHeading(level || 1)
  heading.content = lexicalChildrenToAstInline(node.children || [], defaults)
  return heading
}

/**
 * Converts standard Lexical quote to AST
 */
const lexicalStandardQuoteToAstBlock = (
  node: SerializedElementNode,
  defaults?: { fontFamily?: string; fontSize?: string },
): QuoteBlock => {
  const quote = createQuote()
  quote.content = lexicalChildrenToAstInline(node.children || [], defaults)
  return quote
}

/**
 * Converts standard Lexical list to AST
 */
const lexicalStandardListToAstBlock = (node: any, defaults?: { fontFamily?: string; fontSize?: string }): ListBlock => {
  const ordered = node.listType === 'number'
  const list = createList(ordered)
  list.items = (node.children || [])
    .filter((child: any) => child.type === 'listitem')
    .map((child: any) => lexicalStandardListItemToAstItem(child, ordered, defaults))

  if (list.items.length === 0) {
    list.items = [createListItem()]
  }

  return list
}

/**
 * Converts standard Lexical list item to AST
 */
const lexicalStandardListItemToAstItem = (
  node: any,
  ordered: boolean,
  defaults?: { fontFamily?: string; fontSize?: string },
): ListItem => {
  const item = createListItem()
  item.content = lexicalChildrenToAstInline(node.children || [], defaults)
  return item
}

/**
 * Utility: Creates a Lexical editor state from AST
 */
export const createLexicalStateFromAST = (ast: ContentAST): SerializedEditorState => {
  return astToLexical(ast)
}

/**
 * Utility: Extracts AST from Lexical editor state
 */
export const extractASTFromLexicalState = (
  state: SerializedEditorState,
  metadata?: {
    lang?: 'he' | 'en' | 'mixed'
    dir?: 'rtl' | 'ltr'
    title?: string
    defaultFontFamily?: string
    defaultFontSize?: string
  },
): ContentAST => {
  return lexicalToAst(state, metadata)
}
